import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { User, Calendar, DollarSign, ExternalLink, Loader2 } from 'lucide-react';

const STAGE_CONFIG = {
  new: { label: 'Novo', color: 'hsl(var(--chart-1))' },
  contact: { label: 'Contato', color: 'hsl(var(--chart-2))' },
  scheduled: { label: 'Reunião Agendada', color: 'hsl(var(--chart-3))' },
  proposal: { label: 'Proposta', color: 'hsl(var(--chart-4))' },
  negotiation: { label: 'Negociação', color: 'hsl(var(--chart-5))' },
  won: { label: 'Ganho', color: 'hsl(var(--success))' },
  lost: { label: 'Perdido', color: 'hsl(var(--destructive))' },
};

export default function DealsDetailModal({ 
  open, 
  onOpenChange, 
  deals = [], 
  title,
  subtitle,
  loading = false 
}) {
  const handleDealClick = (dealId) => {
    onOpenChange(false);
    window.open(`/DealDetail?id=${dealId}`, '_blank');
  };

  const formatCurrency = (value) => {
    if (!value) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {title}
            <Badge variant="secondary" className="ml-2">
              {deals.length} deals
            </Badge>
          </DialogTitle>
          {subtitle && (
            <p className="text-sm text-muted-foreground">{subtitle}</p>
          )}
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          {loading ? (
            <div className="py-8 flex flex-col items-center justify-center gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Carregando deals...</p>
            </div>
          ) : deals.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No deals found
            </div>
          ) : (
            <div className="space-y-2">
              {deals.map((deal) => {
                const stage = (deal.stage ?? '').toLowerCase();
                const stageConfig = STAGE_CONFIG[stage] || { label: stage || '-', color: 'hsl(var(--muted))' };
                
                return (
                  <div
                    key={deal.id}
                    onClick={() => handleDealClick(deal.id)}
                    className="p-3 rounded-lg border bg-card hover:bg-accent/50 cursor-pointer transition-colors group"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">
                            {/* Support both RPC format (contact_name) and original format (contact object) */}
                            {deal.contact_name || (deal.contact ? `${deal.contact.first_name} ${deal.contact.last_name}` : deal.name || 'Unknown')}
                          </span>
                          <Badge 
                            variant="outline" 
                            className="text-xs"
                            style={{ 
                              borderColor: stageConfig.color,
                              color: stageConfig.color,
                            }}
                          >
                            {stageConfig.label}
                          </Badge>
                          <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {format(parseISO(deal.created_at), "dd/MM/yyyy", { locale: ptBR })}
                          </span>
                          
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            <span className="opacity-70">Consultant:</span>
                            {/* Support both RPC format (consultant_name) and original format (sales_consultant_user object) */}
                            {deal.consultant_name || (deal.sales_consultant_user 
                              ? (deal.sales_consultant_user.display_name || 
                                 `${deal.sales_consultant_user.first_name} ${deal.sales_consultant_user.last_name}`)
                              : '-')}
                          </span>
                          
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            <span className="opacity-70">SDR:</span>
                            {/* Support both RPC format (sdr_name) and original format (sales_development_user object) */}
                            {deal.sdr_name || (deal.sales_development_user 
                              ? (deal.sales_development_user.display_name || 
                                 `${deal.sales_development_user.first_name} ${deal.sales_development_user.last_name}`)
                              : '-')}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1 text-sm font-semibold text-primary">
                        <DollarSign className="h-4 w-4" />
                        {formatCurrency(deal.effective_amount ?? deal.total_amount)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
